// Resolve a division's format into concrete team-vs-team matchups.
//
// Mirrors the seeding / pool logic in `buildScoresheetDoc` (builders.ts) so the
// games created by "Auto-Populate Games" match the downloaded scoresheet exactly:
// seeds map to the `divisionTeams` array order, and pools come from
// `division.config.pools` (re-seeded 1..k within each pool).
import { buildRoundRobinMatchups } from '@staty/shared/utils/roundRobinSchedule';
import {
  BracketSlotDef,
  getBracketSlots,
  isBracketFormat,
  isConsolationFormat,
  normalizeBracketSize,
  placeholderForSide,
  seedPlaceholder,
} from '@staty/shared/utils/bracketTopology';
import isUnseededTeam from '@staty/shared/utils/isUnseededTeam';

export interface ResolvedMatchup {
  round: number; // 1-indexed round number (within its pool for pool formats)
  pool?: number; // 0-based pool index, pool formats only
  homeTeamId: string;
  awayTeamId: string;
}

export interface ResolveMatchupsData {
  division: any;
  teams: any[]; // divisionTeams, in the same order the scoresheet uses
}

// Formats whose matchups are real, fully-determined seed-vs-seed pairings.
const SUPPORTED_FORMATS = new Set([
  'round-robin',
  'round-robin-playoffs',
  'pool-play',
  'pool-play-playoffs',
]);

export function canAutoPopulate(format?: string | null): boolean {
  return !!format && SUPPORTED_FORMATS.has(format);
}

const teamId = (t: any): string | undefined =>
  t && (t._id ?? t.id) ? String(t._id ?? t.id) : undefined;

// Round-robin over an ordered list of teams → matchups tagged with `pool`.
function roundRobinOverTeams(
  orderedTeams: any[],
  roundsPerMatchup: number,
  pool: number | undefined,
): ResolvedMatchup[] {
  // Seeds map to array order; teams without an id are kept as placeholders so
  // the seed numbering (and therefore the pairings) matches the scoresheet,
  // then dropped from the output like before.
  const ids = orderedTeams.map((t, i) => teamId(t) ?? `__missing-${i}`);
  return buildRoundRobinMatchups(ids, roundsPerMatchup)
    .filter((m) => !m.homeTeamId.startsWith('__missing-') && !m.awayTeamId.startsWith('__missing-'))
    .map(({ round, homeTeamId, awayTeamId }) => ({ round, pool, homeTeamId, awayTeamId }));
}

// One game per bracket slot for the standalone playoff formats
// ('playoffs' / 'bracket' / 'playoffs-consolation').
export interface ResolvedBracketGame {
  bracketSlot: string;
  stage: string;
  playoffRound: string;
  gameLabel: string;
  homeTeamId?: string;
  awayTeamId?: string;
  homeTeamPlaceholder?: string;
  awayTeamPlaceholder?: string;
}

export function canAutoPopulateBracket(format?: string | null): boolean {
  return isBracketFormat(format);
}

// Map bracket slots to one game each. First-round (seed-fed) slots take the
// team at that seed position when one exists, else a "Seed N" placeholder;
// later rounds get "Winner of …" / "Loser of …" placeholders and are filled by
// the API's auto-advancement as results come in.
function slotsToGames(
  slots: BracketSlotDef[],
  size: number,
  consolation: boolean,
  seededTeams: any[],
): ResolvedBracketGame[] {
  return slots.map((slot) => {
    const game: ResolvedBracketGame = {
      bracketSlot: slot.key,
      stage: slot.stage,
      playoffRound: slot.playoffRound,
      gameLabel: slot.label,
    };
    if (slot.seeds) {
      const [homeSeed, awaySeed] = slot.seeds;
      game.homeTeamId = teamId(seededTeams[homeSeed - 1]);
      game.awayTeamId = teamId(seededTeams[awaySeed - 1]);
      if (!game.homeTeamId) game.homeTeamPlaceholder = seedPlaceholder(homeSeed);
      if (!game.awayTeamId) game.awayTeamPlaceholder = seedPlaceholder(awaySeed);
    } else {
      game.homeTeamPlaceholder = placeholderForSide(size, consolation, slot.key, 'home');
      game.awayTeamPlaceholder = placeholderForSide(size, consolation, slot.key, 'away');
    }
    return game;
  });
}

// Resolve a standalone bracket format into one game per slot. First-round
// slots are filled from the division's seed order (unseeded teams last, same
// comparator as the Sort by Seed UI); seed positions with no registered team
// yet get "Seed N" placeholders so the full bracket can be generated right
// after the format is chosen.
export function resolveBracketGames(
  format: string | undefined | null,
  data: ResolveMatchupsData,
): ResolvedBracketGame[] {
  if (!canAutoPopulateBracket(format)) return [];
  const consolation = isConsolationFormat(format);
  const size = normalizeBracketSize(data?.division?.config?.playoffTeams, consolation);

  const seeded = [...(data?.teams || [])].sort(
    (a, b) => (isUnseededTeam(a?.seed) ? Infinity : a.seed) - (isUnseededTeam(b?.seed) ? Infinity : b.seed),
  );
  return slotsToGames(getBracketSlots(size, consolation), size, consolation, seeded);
}

// Resolve the playoff bracket appended to a hybrid format (round-robin-playoffs /
// pool-play-playoffs) into one all-placeholder game per slot. Round-1 sides are
// "Seed N" — the actual teams come from group-stage standings, assigned by the
// admin once the group stage is decided. Hybrid playoff brackets are never
// consolation-style.
export function resolvePlayoffBracketGames(size: number | undefined): ResolvedBracketGame[] {
  const normalized = normalizeBracketSize(size, false);
  return slotsToGames(getBracketSlots(normalized, false), normalized, false, []);
}

export function resolveMatchups(
  format: string | undefined | null,
  data: ResolveMatchupsData,
): ResolvedMatchup[] {
  if (!canAutoPopulate(format)) return [];
  const teams = data?.teams || [];
  const roundsPerMatchup = data?.division?.config?.roundsPerMatchup || 1;

  if (format === 'round-robin' || format === 'round-robin-playoffs') {
    if (teams.length < 2) return [];
    return roundRobinOverTeams(teams, roundsPerMatchup, undefined);
  }

  // pool-play / pool-play-playoffs — round-robin within each configured pool.
  const configPools = data?.division?.config?.pools;
  if (!Array.isArray(configPools) || !configPools.length) return [];

  const byId = new Map<string, any>();
  teams.forEach((t) => {
    const id = teamId(t);
    if (id) byId.set(id, t);
  });

  const out: ResolvedMatchup[] = [];
  configPools.forEach((p: any, poolIdx: number) => {
    const poolTeams = (p?.teamIds || [])
      .map((id: string) => byId.get(String(id)))
      .filter(Boolean);
    if (poolTeams.length < 2) return;
    out.push(...roundRobinOverTeams(poolTeams, roundsPerMatchup, poolIdx));
  });
  return out;
}
